<template><div><code-well-header>
  <dt-stack direction="row" gap="500">
    <example-presence presence="active"/>
    <example-presence presence="away"/>
    <example-presence presence="busy"/>
    <example-presence presence="offline"/>
  </dt-stack>
</code-well-header>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Located at the bottom right of an avatar, the <code v-pre>presence</code> indicator displays a user's current availablity. At a glance, check if a user is available, in a meeting, on a call, away, offline, or set to 'Do Not Disturb'. It's important to note that this indicator is automatically set and cannot be changed manually by the user.</p>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="active" tabindex="-1" class="d-docsite--header-3">Active <a class="header-anchor d-link d-docsite--link d-link" href="#active" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When a user is available.</p>
<code-well-header>
  <example-presence presence="active" ref="activeExample"/>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.activeExample'
vueCode='
<dt-presence presence="active"  />
'
showHtmlWarning />
<h3 id="busy" tabindex="-1" class="d-docsite--header-3">Busy <a class="header-anchor d-link d-docsite--link d-link" href="#busy" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When a user is unavailable, either due to being <strong>'On a call'</strong>, <strong>'In a meeting'</strong>, or set to <strong>'DND (Do Not Disturb)'</strong>. Additionally, a text label indicating their specific status will appear under the user's name.</p>
<code-well-header>
  <example-presence presence="busy" ref="busyExample"/>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.busyExample'
vueCode='
<dt-presence presence="busy" />
'
showHtmlWarning />
<h3 id="away" tabindex="-1" class="d-docsite--header-3">Away <a class="header-anchor d-link d-docsite--link d-link" href="#away" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When a user has a scheduled meeting on their synced calendar (Google G Suite or Microsoft Office 365) and is not actively participating in it through the app. Additionally, <strong>'In a meeting'</strong> will appear under the user's name.</p>
<code-well-header>
  <example-presence presence="away" ref="awayExample"/>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.awayExample'
vueCode='
<dt-presence presence="away" />
'
showHtmlWarning />
<h3 id="offline" tabindex="-1" class="d-docsite--header-3">Offline <a class="header-anchor d-link d-docsite--link d-link" href="#offline" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When a user has not logged in for their first time.</p>
<code-well-header>
  <example-presence presence="offline" ref="offlineExample"/>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.offlineExample'
vueCode='
<dt-presence presence="offline" />
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="presence" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="presence" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">You may wish to announce any live changes to this component via the screen reader since this is only a visual indicator.</p>
<p class="d-docsite--paragraph">If you'd like for a screen reader to read out any changes to Presence, you should pass the <code v-pre>srText</code> prop so it is
read by AT and set the <code v-pre>aria-live</code> attribute to either 'polite' or 'assertive'.
Even though the component has a role of &quot;status&quot; to assist SR apps in reading out status changes, its default
'aria-live' value is set to 'off'.</p>
<p class="d-docsite--paragraph"><a href="https://www.w3.org/WAI/WCAG22/Techniques/aria/ARIA22" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">See W3C guidelines</a> for more information.</p>
<p class="d-docsite--paragraph">Example:</p>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>dt-presence</span></span>
<span class="line">  <span class="token attr-name">presence</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>active<span class="token punctuation">"</span></span></span>
<span class="line">  <span class="token attr-name">sr-text</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>User {{ user }} is active<span class="token punctuation">"</span></span></span>
<span class="line"><span class="token punctuation">/></span></span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">Abbreviations / symbols should be read out in full for voiceover / screen readers.</p>
</div></template>


<script setup>
  import ExamplePresence from '@exampleComponents/ExamplePresence.vue';
</script>